import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, Star, Trash2, Upload, X } from 'lucide-react';
import { useEditor } from "../../../../../../App.jsx";
import getMediaUrl, { getDefaultProductImage } from "../../../../../../components/tools/utils.jsx";
import { confirmDelete, showAlert } from "../../../../../../components/HelperAlerts/HelpersAlerts.jsx";
import { getThemeContext } from "../../../../../../components/providers/ThemeProvider.jsx";
import { useCartContext } from "../../../../../../components/providers/CartProvider.jsx";

const initialProductState = {
    name: '',
    description: '',
    price: '',
    stock: '',
    category: '',
};

const formatApiError = (error, fallback) => (
    error?.response?.data ? JSON.stringify(error.response.data) : fallback
);

const ImageCard = ({
    src,
    label,
    isDefault,
    onSetDefault,
    onRemove,
    canRemove = true,
}) => (
    <div className="relative group rounded-xl overflow-hidden bg-base-200 border border-base-content/10 min-h-[140px]">
        <img
            src={src}
            alt={label}
            className="w-full h-40 object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent flex items-center justify-between gap-2">
            <span className="text-xs text-white truncate">{label}</span>
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    className={`btn btn-xs btn-circle ${isDefault ? 'btn-warning' : 'btn-ghost bg-black/30 text-white hover:bg-black/50'}`}
                    onClick={onSetDefault}
                    title={isDefault ? 'Imagen principal' : 'Marcar como principal'}
                    disabled={isDefault}
                >
                    <Star size={13} fill={isDefault ? 'currentColor' : 'none'} />
                </button>
                {canRemove && (
                    <button
                        type="button"
                        className="btn btn-xs btn-circle btn-error"
                        onClick={onRemove}
                        title="Eliminar imagen"
                    >
                        <Trash2 size={13} />
                    </button>
                )}
            </div>
        </div>
        {isDefault && (
            <span className="absolute top-2 left-2 badge badge-warning badge-sm">
                Principal
            </span>
        )}
    </div>
);

const PendingImageCard = ({ image, isDefault, onSetDefault, onRemove }) => (
    <div className="relative group rounded-xl overflow-hidden bg-base-200 border border-dashed border-base-content/20 min-h-[140px]">
        <img
            src={image.preview}
            alt={image.file.name}
            className="w-full h-40 object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent flex items-center justify-between gap-2">
            <span className="text-xs text-white truncate">{image.file.name}</span>
            <div className="flex items-center gap-1">
                <button
                    type="button"
                    className={`btn btn-xs btn-circle ${isDefault ? 'btn-warning' : 'btn-ghost bg-black/30 text-white hover:bg-black/50'}`}
                    onClick={onSetDefault}
                    title={isDefault ? 'Imagen principal' : 'Marcar como principal'}
                    disabled={isDefault}
                >
                    <Star size={13} fill={isDefault ? 'currentColor' : 'none'} />
                </button>
                <button
                    type="button"
                    className="btn btn-xs btn-circle btn-error"
                    onClick={onRemove}
                    title="Quitar imagen pendiente"
                >
                    <X size={13} />
                </button>
            </div>
        </div>
        <span className="absolute top-2 left-2 badge badge-info badge-sm">
            Pendiente
        </span>
    </div>
);

const ProductoEditor = ({ editorID }) => {
    const [product, setProduct] = useState(initialProductState);
    const [productImages, setProductImages] = useState([]);
    const [pendingImages, setPendingImages] = useState([]);
    const [errors, setErrors] = useState({});
    const [categories, setCategories] = useState([]);
    const [saving, setSaving] = useState(false);
    const pendingImagesRef = useRef([]);

    const { stateEditor, setStateEditor } = useEditor();
    const { fetchCart } = useCartContext();
    const theme = getThemeContext();
    const isCreateMode = editorID === 'add';

    const hasPendingDefault = pendingImages.some((image) => image.is_default);
    const defaultPreview = useMemo(() => {
        const pendingDefault = pendingImages.find((image) => image.is_default);
        if (pendingDefault) return pendingDefault.preview;
        const defaultImage = getDefaultProductImage({ images: productImages });
        return defaultImage ? getMediaUrl(defaultImage) : null;
    }, [pendingImages, productImages]);

    const fetchProduct = async () => {
        if (!editorID || isCreateMode) return;
        const response = await axios.get(`/products/${editorID}/`);
        setProduct({
            ...initialProductState,
            ...response.data,
            category: response.data.category ?? '',
        });
        setProductImages(response.data.images ?? []);
    };

    useEffect(() => {
        axios
            .get('/categories/')
            .then((response) => {
                setCategories(Array.isArray(response.data) ? response.data : (response.data.results ?? []));
            })
            .catch((error) => {
                console.error('Error fetching categories:', error);
            });
    }, []);

    useEffect(() => {
        fetchProduct().catch((error) => {
            console.error('Error fetching product:', error);
        });
    }, [editorID]);

    useEffect(() => {
        pendingImagesRef.current = pendingImages;
    }, [pendingImages]);

    useEffect(() => () => {
        pendingImagesRef.current.forEach((image) => URL.revokeObjectURL(image.preview));
    }, []);

    const validate = () => {
        const newErrors = {};
        if (!product.name.trim()) newErrors.name = 'El nombre es obligatorio.';
        if (!product.description.trim()) newErrors.description = 'La descripción es obligatoria.';
        if (!product.price || isNaN(product.price)) newErrors.price = 'El precio debe ser un número válido.';
        if (product.stock === '' || product.stock === null || isNaN(product.stock)) {
            newErrors.stock = 'El stock debe ser un número válido.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setProduct((prevProduct) => ({
            ...prevProduct,
            [name]: value,
        }));
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files ?? []);
        if (!files.length) return;

        const shouldMarkFirstAsDefault = productImages.length === 0 && pendingImages.length === 0;
        const nextImages = files.map((file, index) => ({
            id: `${file.name}-${file.lastModified}-${index}-${Date.now()}`,
            file,
            preview: URL.createObjectURL(file),
            is_default: shouldMarkFirstAsDefault && index === 0,
        }));

        setPendingImages((prev) => [...prev, ...nextImages]);
        e.target.value = '';
    };

    const removePendingImage = (imageId) => {
        setPendingImages((prev) => {
            const imageToRemove = prev.find((image) => image.id === imageId);
            if (imageToRemove) URL.revokeObjectURL(imageToRemove.preview);

            const next = prev.filter((image) => image.id !== imageId);
            if (imageToRemove?.is_default && next.length > 0 && !productImages.some((image) => image.is_default)) {
                next[0] = { ...next[0], is_default: true };
            }
            return next;
        });
    };

    const setPendingDefault = (imageId) => {
        setPendingImages((prev) => prev.map((image) => ({
            ...image,
            is_default: image.id === imageId,
        })));
    };

    const setExistingDefault = async (image) => {
        if (image.is_default && !hasPendingDefault) return;

        try {
            await axios.patch(`/product-images/${image.id}/`, { is_default: true });
            setPendingImages((prev) => prev.map((pending) => ({ ...pending, is_default: false })));
            await fetchProduct();
            await fetchCart?.();
        } catch (error) {
            console.error('Error setting default image:', error?.response?.data ?? error);
            showAlert({
                title: 'Error',
                text: formatApiError(error, 'No se pudo marcar la imagen principal.'),
                icon: 'error',
                theme,
            });
        }
    };

    const deleteExistingImage = async (image) => {
        const confirmed = await confirmDelete({
            title: 'Eliminar imagen',
            text: 'Esta imagen se eliminará del producto.',
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar',
            theme,
        });
        if (!confirmed) return;

        try {
            await axios.delete(`/product-images/${image.id}/`);
            await fetchProduct();
            await fetchCart?.();
        } catch (error) {
            console.error('Error deleting product image:', error?.response?.data ?? error);
            showAlert({
                title: 'Error',
                text: formatApiError(error, 'No se pudo eliminar la imagen.'),
                icon: 'error',
                theme,
            });
        }
    };

    const buildProductPayload = () => {
        const formData = new FormData();
        formData.append('name', product.name);
        formData.append('description', product.description);
        formData.append('price', product.price);
        formData.append('stock', product.stock);
        if (product.category !== '' && product.category !== null && product.category !== undefined) {
            formData.append('category', product.category);
        }
        return formData;
    };

    const uploadPendingImages = async (productId) => {
        for (const image of pendingImages) {
            const imageData = new FormData();
            imageData.append('product', productId);
            imageData.append('image', image.file);
            imageData.append('is_default', image.is_default ? 'true' : 'false');
            await axios.post('/product-images/', imageData);
        }
    };

    const handleBack = () => {
        setStateEditor({
            ...stateEditor,
            editor: false,
            editorType: null,
            editorID: null,
        });
    };

    const resetForAnotherProduct = () => {
        pendingImages.forEach((image) => URL.revokeObjectURL(image.preview));
        setProduct(initialProductState);
        setProductImages([]);
        setPendingImages([]);
        setErrors({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate() || saving) return;

        setSaving(true);
        try {
            const response = isCreateMode
                ? await axios.post('/products/', buildProductPayload())
                : await axios.put(`/products/${editorID}/`, buildProductPayload());

            const savedProductId = response.data.id;
            if (pendingImages.length > 0) {
                await uploadPendingImages(savedProductId);
            }
            await fetchCart?.();

            pendingImages.forEach((image) => URL.revokeObjectURL(image.preview));
            setPendingImages([]);

            const result = await showAlert({
                title: isCreateMode ? 'Producto creado' : 'Producto modificado',
                text: isCreateMode ? '¿Qué desea hacer ahora?' : '¿Desea volver a la lista?',
                icon: 'success',
                showCancelButton: true,
                showDenyButton: isCreateMode,
                confirmButtonText: 'Volver a la lista',
                denyButtonText: 'Añadir otro producto',
                cancelButtonText: 'Quedarse aquí',
                theme,
            });

            if (result.isConfirmed) {
                setStateEditor({ ...stateEditor, editor: false, editorType: null, editorID: null });
                return;
            }

            if (isCreateMode && result.isDenied) {
                resetForAnotherProduct();
                return;
            }

            if (isCreateMode && result.isDismissed) {
                setStateEditor({
                    ...stateEditor,
                    editor: true,
                    editorType: 'producto_editor',
                    editorID: savedProductId,
                });
                return;
            }

            if (!isCreateMode) {
                await fetchProduct();
            }
        } catch (error) {
            console.error('Error saving product:', error?.response?.data ?? error);
            showAlert({
                title: 'Error',
                text: formatApiError(error, isCreateMode ? 'Hubo un problema al crear el producto.' : 'Hubo un problema al modificar el producto.'),
                icon: 'error',
                theme,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="w-full h-full min-h-full flex flex-col">
            <div className="px-6 pt-4 flex items-center justify-between gap-3">
                <button
                    type="button"
                    className="btn btn-ghost btn-sm gap-2"
                    onClick={handleBack}
                >
                    <ArrowLeft size={16} />
                    Volver
                </button>
                <span className="text-sm text-base-content/60">
                    {isCreateMode ? 'Nuevo producto' : 'Editar producto'}
                </span>
            </div>
            <form className="flex-1 min-h-0 w-full" onSubmit={handleSubmit}>
                <div className="p-6 flex flex-col lg:flex-row gap-4 h-full min-h-full">
                    <section className="flex flex-col w-full lg:w-3/5 h-full min-h-[360px]">
                        <div className="glassCard rounded-xl p-4 h-full overflow-y-auto">
                            <div className="flex items-center justify-between gap-3 mb-4">
                                <div>
                                    <h2 className="text-lg font-bold text-base-content">Imágenes del producto</h2>
                                    <p className="text-xs text-base-content/60">Sube varias imágenes, marca la principal o elimina las que no quieras.</p>
                                </div>
                                <label className="btn btn-sm btn-primary gap-2 shrink-0">
                                    <Upload size={15} />
                                    Subir
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleImageChange}
                                        className="hidden"
                                        accept="image/png, image/jpeg, image/jpg, image/webp"
                                    />
                                </label>
                            </div>

                            {defaultPreview && (
                                <div className="mb-4 rounded-xl overflow-hidden bg-base-200 border border-base-content/10">
                                    <img src={defaultPreview} alt="Imagen principal del producto" className="w-full h-56 object-contain bg-base-300" />
                                </div>
                            )}

                            {(productImages.length > 0 || pendingImages.length > 0) ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                                    {productImages.map((image) => (
                                        <ImageCard
                                            key={image.id}
                                            src={getMediaUrl(image.image)}
                                            label={`Imagen ${image.id}`}
                                            isDefault={!hasPendingDefault && image.is_default}
                                            onSetDefault={() => setExistingDefault(image)}
                                            onRemove={() => deleteExistingImage(image)}
                                        />
                                    ))}
                                    {pendingImages.map((image) => (
                                        <PendingImageCard
                                            key={image.id}
                                            image={image}
                                            isDefault={image.is_default}
                                            onSetDefault={() => setPendingDefault(image.id)}
                                            onRemove={() => removePendingImage(image.id)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <label className="min-h-[260px] rounded-xl border border-dashed border-base-content/25 bg-base-200 flex flex-col items-center justify-center gap-3 cursor-pointer hover:border-primary transition-colors">
                                    <Upload size={34} className="text-base-content/40" />
                                    <span className="text-base-content/60 text-sm">Seleccionar imágenes del producto</span>
                                    <input
                                        type="file"
                                        multiple
                                        onChange={handleImageChange}
                                        className="hidden"
                                        accept="image/png, image/jpeg, image/jpg, image/webp"
                                    />
                                </label>
                            )}
                        </div>
                    </section>

                    <section className="flex flex-col w-full lg:w-2/5 h-full">
                        <div className="flex-1 p-3 flex-col overflow-y-auto">
                            <div className="mb-4">
                                <label className="text-base-content/70 text-lg font-bold mb-2 block">Nombre del producto</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={product.name}
                                    onChange={handleInputChange}
                                    className="input input-bordered w-full"
                                    placeholder="Nombre del producto"
                                />
                                {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                            </div>
                            <div className="mb-4">
                                <label className="text-base-content/70 text-lg font-bold mb-2 block">Descripción</label>
                                <textarea
                                    name="description"
                                    value={product.description}
                                    onChange={handleInputChange}
                                    className="textarea textarea-bordered w-full min-h-28"
                                    placeholder="Descripción del producto"
                                />
                                {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
                            </div>
                            <div className="mb-4">
                                <label className="text-base-content/70 text-lg font-bold mb-2 block">Precio</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    name="price"
                                    value={product.price}
                                    onChange={handleInputChange}
                                    className="input input-bordered w-full"
                                    placeholder="Precio del producto"
                                />
                                {errors.price && <p className="text-red-500 text-sm">{errors.price}</p>}
                            </div>
                            <div className="mb-4">
                                <label className="text-base-content/70 text-lg font-bold mb-2 block">Stock</label>
                                <input
                                    type="number"
                                    name="stock"
                                    value={product.stock}
                                    onChange={handleInputChange}
                                    className="input input-bordered w-full"
                                    placeholder="Stock del producto"
                                />
                                {errors.stock && <p className="text-red-500 text-sm">{errors.stock}</p>}
                            </div>
                            <div className="mb-4">
                                <label className="text-base-content/70 text-lg font-bold mb-2 block">Categoría</label>
                                <select
                                    name="category"
                                    value={product.category ?? ''}
                                    onChange={handleInputChange}
                                    className="select select-bordered w-full"
                                >
                                    <option value="">Sin categoría</option>
                                    {categories.map((category) => (
                                        <option key={category.id} value={category.id}>
                                            {category.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.category && <p className="text-red-500 text-sm">{errors.category}</p>}
                            </div>
                        </div>

                        <div className="flex bottom-0 pt-3">
                            <button
                                type="submit"
                                className="btn btn-primary w-full text-lg text-primary-content"
                                disabled={saving}
                            >
                                {saving && <span className="loading loading-spinner loading-sm" />}
                                {isCreateMode ? 'Añadir Producto' : 'Modificar Producto'}
                            </button>
                        </div>
                    </section>
                </div>
            </form>
        </div>
    );
};

export default ProductoEditor;
